import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Badge } from 'primereact/badge';
import React, { useState } from 'react';
import {
    AppWindowIcon,
    BodySmallRegular,
    IconButton,
    Maximize2Icon,
    MessageCircleIcon,
    Minimize2Icon,
    PanelLeftIcon,
    PanelModal,
    Spinner,
    type StackProps,
    useMedia,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { Button } from '../../../common/components/GroveButtons';
import { PageContentContainer } from '../../../common/components/GroveContainers';
import { atomWithStorage } from '../../../common/providers/StorageProvider';
import { StorageKey } from '../../../common/types/Storage';
import { appRightEdge } from '../../../common/utils/layout';
import { GuideMessagingChannel } from '../../../guide-messaging/components/GuideMessagingChannel';
import { useGetSendbirdChannel } from '../../../guide-messaging/hooks/useGetSendbirdChannel';
import { TrackedEvent } from '../../../logger/Events';
import { trackEvent } from '../../../logger/Logger';
import type { MembershipDetails } from '../../entityTypes';

export const membershipMessagingViewModeAtom = atomWithStorage<
    'modal' | 'panel'
>(StorageKey.MEMBERSHIP_MESSAGING_VIEW_MODE, 'modal');
export const membershipMessagingSizeAtom = atom<'normal' | 'large'>('normal');
export const showMembershipMessagingModalAtom = atom<boolean>(false);

interface MembershipMessagingProps extends StackProps {
    membership: MembershipDetails;
}
interface MembershipMessagingPanelProps
    extends MembershipMessagingProps, StackProps {}

const MembershipMessagingChannel = ({
    membership,
    ...stackProps
}: MembershipMessagingProps) => {
    const { channel, isLoading } = useGetSendbirdChannel(membership);

    if (isLoading) {
        return (
            <View minWidth={400}>
                <Spinner />
            </View>
        );
    }

    if (!channel) {
        return (
            <BodySmallRegular alignSelf="center" padding={16}>
                This membership&apos;s messaging channel could not be found
            </BodySmallRegular>
        );
    }

    return (
        <YStack height="100%" {...stackProps}>
            <GuideMessagingChannel
                sendbirdChannel={channel}
                headerTextSize="small"
                shouldShowLinkToMembershipDetails={false}
            />
        </YStack>
    );
};

export const MembershipMessagingPanel = ({
    membership,
    ...stackProps
}: MembershipMessagingPanelProps) => {
    const { channel } = useGetSendbirdChannel(membership);
    const channelUnreadCount = channel?.unreadMessageCount ?? 0;
    const [shouldShowChannel, setShouldShowChannel] = useState(
        channelUnreadCount === 0,
    );
    const messagingSize = useAtomValue(membershipMessagingSizeAtom);

    return (
        <PageContentContainer
            {...stackProps}
            height="85vh"
            minWidth={messagingSize === 'large' ? 700 : 575}
            flexBasis={messagingSize === 'large' ? '50%' : '30%'}
            alignItems="stretch"
        >
            {/* If the channel has any unread messages, we initially do not render the channel
                until the user wishes to show the channel (and thus have the messages marked as read) */}
            {shouldShowChannel ? (
                <MembershipMessagingChannel
                    membership={membership}
                    marginHorizontal={-20} // Effectively reduces padding of the container component in a simple way
                />
            ) : (
                <YStack alignItems="center">
                    <XStack justifyContent="flex-end">
                        <OpenMessagingButton
                            unreadCount={channelUnreadCount}
                            onPress={() => {
                                setShouldShowChannel(true);
                            }}
                        />
                    </XStack>
                    <div
                        style={{
                            fontSize: 14,
                            textAlign: 'center',
                            marginTop: '.5rem',
                            lineHeight: '1.5rem',
                        }}
                    >
                        <strong>{channelUnreadCount} unread messages</strong>{' '}
                        <br />
                        <em>
                            When you&apos;re ready to read them, click the
                            button above
                        </em>
                    </div>
                </YStack>
            )}
            <MessagingViewButtons />
        </PageContentContainer>
    );
};

export const MembershipMessagingModal = ({
    membership,
}: MembershipMessagingProps) => {
    const [showMessagingModal, setShowMessagingModal] = useAtom(
        showMembershipMessagingModalAtom,
    );
    const messagingSize = useAtomValue(membershipMessagingSizeAtom);

    return (
        <PanelModal
            isOpen={showMessagingModal}
            onClose={() => {
                setShowMessagingModal(false);
            }}
            contentProps={{
                height: '80vh',
                width: '80vw',
                maxHeight: messagingSize === 'large' ? 900 : 700,
                maxWidth: messagingSize === 'large' ? 900 : 550,
                style: { position: 'fixed', top: 130, right: appRightEdge(24) },
            }}
            hasOverlay={false}
        >
            <MembershipMessagingChannel membership={membership} />
            <MessagingViewButtons />
        </PanelModal>
    );
};

const MessagingViewButtons = () => {
    const [messagingSize, setMessagingSize] = useAtom(
        membershipMessagingSizeAtom,
    );
    const [messagingViewMode, setMessagingViewMode] = useAtom(
        membershipMessagingViewModeAtom,
    );
    const setShowMessagingModal = useSetAtom(showMembershipMessagingModalAtom);
    const breakpoints = useMedia();
    const showViewModeButton =
        messagingViewMode === 'panel' ||
        (messagingViewMode === 'modal' && breakpoints.lg);

    return (
        <XStack
            justifyContent="flex-end"
            position="absolute"
            top={messagingViewMode === 'modal' ? 6 : 12}
            right={messagingViewMode === 'modal' ? 6 : 12}
        >
            <IconButton
                variant="tertiary"
                icon={
                    messagingSize === 'normal' ? Maximize2Icon : Minimize2Icon
                }
                onPress={() => {
                    setMessagingSize((prev) => {
                        const newValue = prev === 'normal' ? 'large' : 'normal';
                        trackEvent(
                            TrackedEvent.MembershipMessagingSizeToggled,
                            {
                                size: newValue,
                            },
                        );
                        return newValue;
                    });
                }}
            />
            {showViewModeButton && (
                <IconButton
                    variant="tertiary"
                    icon={
                        messagingViewMode === 'modal'
                            ? PanelLeftIcon
                            : AppWindowIcon
                    }
                    onPress={() => {
                        setMessagingViewMode((prev) => {
                            const newValue =
                                prev === 'modal' ? 'panel' : 'modal';
                            if (newValue === 'panel') {
                                setShowMessagingModal(false);
                            }
                            trackEvent(
                                TrackedEvent.MembershipMessagingViewModeToggled,
                                {
                                    mode: newValue,
                                },
                            );
                            return newValue;
                        });
                    }}
                />
            )}
        </XStack>
    );
};

export const MembershipMessagingModalButton = ({
    membership,
}: MembershipMessagingProps) => {
    const setShowMessagingModal = useSetAtom(showMembershipMessagingModalAtom);
    const { channel } = useGetSendbirdChannel(membership);
    const channelUnreadCount = channel?.unreadMessageCount ?? 0;

    return (
        <OpenMessagingButton
            unreadCount={channelUnreadCount}
            onPress={() => {
                setShowMessagingModal(true);
            }}
        />
    );
};

const OpenMessagingButton = ({
    onPress,
    unreadCount,
}: {
    onPress: () => void;
    unreadCount: number;
}) => {
    return (
        <div className="p-overlay-badge">
            <Button size="small" icon={MessageCircleIcon} onPress={onPress}>
                Open Messaging
            </Button>
            {unreadCount > 0 && <Badge value={unreadCount} severity="danger" />}
        </div>
    );
};
